import type { FunctionComponent } from 'react';

import { Alert, Badge, Box, Button, Divider, Group, Modal, Stack, Text } from '@mantine/core';

import { SUPPORT_CONTACT_URL } from 'constants/chownow';
import { useTranslation } from 'i18n';

import { CheckIconXs, CloseIconXs } from '@chownow/pantry/icons';
import type { BuildStatus, StepState } from './types';

type MenuProgressModalProps = {
  lastActiveStatus: BuildStatus | null;
  menuName: string;
  onClose: () => void;
  onDone: () => void;
  onReUpload: () => void;
  onContactSupport?: () => void;
  opened: boolean;
  status: BuildStatus | null;
};

const NS = 'menuUpload';

const STEP_KEYS = [
  { key: 'processing', labelKey: 'MenuProgressModal.stepProcessing' },
  { key: 'importing', labelKey: 'MenuProgressModal.stepImporting' },
  { key: 'creating', labelKey: 'MenuProgressModal.stepCreatingMenu' },
  { key: 'finalizing', labelKey: 'MenuProgressModal.stepFinalizing' },
] as const;

const STATUS_TO_COMPLETED_INDEX: Record<string, number> = {
  started: -1,
  file_uploaded: 0,
  file_processed: 1,
  menu_created: 2,
  menu_inserted: 3,
  finished: 3,
};

const getStepStates = (
  status: BuildStatus | null,
  lastActiveStatus: BuildStatus | null,
): StepState[] => {
  if (!status) return ['active', 'pending', 'pending', 'pending'];

  if (status === 'failed') {
    const completedUpTo = lastActiveStatus
      ? (STATUS_TO_COMPLETED_INDEX[lastActiveStatus] ?? -1)
      : -1;
    return STEP_KEYS.map((_, i) => {
      if (i < completedUpTo + 1) return 'done';
      if (i === completedUpTo + 1) return 'error';
      return 'pending';
    });
  }

  const completedUpTo = STATUS_TO_COMPLETED_INDEX[status] ?? -1;
  return STEP_KEYS.map((_, i) => {
    if (i <= completedUpTo) return 'done';
    if (i === completedUpTo + 1) return 'active';
    return 'pending';
  });
};

const StepIndicator: FunctionComponent<{ state: StepState }> = ({ state }) => {
  const circleBase = {
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (state === 'done') {
    return (
      <Box
        w={24}
        h={24}
        style={{
          ...circleBase,
          border: '1px solid var(--border-primary)',
          backgroundColor: 'var(--surface-success)',
        }}
      >
        <CheckIconXs />
      </Box>
    );
  }

  if (state === 'active') {
    return (
      <Box
        w={24}
        h={24}
        style={{
          ...circleBase,
          border: '2px solid var(--content-primary)',
        }}
      >
        <Box
          w={16}
          h={16}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--content-primary)',
          }}
        />
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box
        w={24}
        h={24}
        style={{
          ...circleBase,
          border: '1px solid var(--border-primary)',
          backgroundColor: 'var(--surface-caution)',
        }}
      >
        <CloseIconXs />
      </Box>
    );
  }

  return (
    <Box
      w={24}
      h={24}
      style={{
        ...circleBase,
        border: '1px solid var(--border-secondary)',
      }}
    />
  );
};

const TrackLine: FunctionComponent<{ solid: boolean }> = ({ solid }) => (
  <Box w={24} h={32} style={{ display: 'flex', justifyContent: 'center' }}>
    <Box
      w={1}
      h="100%"
      style={{
        borderLeft: solid
          ? '1px solid var(--border-primary)'
          : '1px dashed var(--border-secondary)',
      }}
    />
  </Box>
);

const STEP_BADGE_CONFIG = {
  active: { variant: 'blue', labelKey: 'MenuProgressModal.inProgress' },
  done: { variant: 'success', labelKey: 'MenuProgressModal.done' },
  error: { variant: 'caution', labelKey: 'MenuProgressModal.error' },
  pending: { variant: 'gray', labelKey: 'MenuProgressModal.pending' },
} as const;

export const MenuProgressModal: FunctionComponent<MenuProgressModalProps> = ({
  lastActiveStatus,
  menuName,
  onClose,
  onDone,
  onReUpload,
  onContactSupport,
  opened,
  status,
}) => {
  const { t } = useTranslation(NS);
  const stepStates = getStepStates(status, lastActiveStatus);
  const isComplete = status === 'menu_inserted' || status === 'finished';
  const isFailed = status === 'failed';

  return (
    <Modal opened={opened} onClose={onClose} title={t('MenuProgressModal.modalTitle')}>
      <Stack gap="md">
        <Text>{t('MenuProgressModal.currentStatus', { menuName })}</Text>

        <Divider />

        <Stack gap={0} pb="md">
          {STEP_KEYS.map((step, i) => {
            const badge = STEP_BADGE_CONFIG[stepStates[i]];
            return (
              <Box key={step.key}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap="sm" align="center" wrap="nowrap">
                    <StepIndicator state={stepStates[i]} />
                    <Text variant="caption-sm" tt="uppercase">
                      {t(step.labelKey)}
                    </Text>
                  </Group>
                  <Badge variant={badge.variant} size="xs">
                    {t(badge.labelKey)}
                  </Badge>
                </Group>
                {i < STEP_KEYS.length - 1 && (
                  <TrackLine solid={stepStates[i] === 'done' && stepStates[i + 1] !== 'pending'} />
                )}
              </Box>
            );
          })}
        </Stack>

        {isFailed && <Alert variant="caution">{t('MenuProgressModal.errorMessage')}</Alert>}

        <Stack gap="xs">
          {isFailed ? (
            <>
              <Button onClick={onReUpload} fullWidth>
                {t('MenuProgressModal.tryAgain')}
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  window.open(SUPPORT_CONTACT_URL, '_blank', 'noopener');
                  onContactSupport?.();
                }}
              >
                {t('MenuProgressModal.contactSupport')}
              </Button>
            </>
          ) : (
            <Button onClick={onDone} fullWidth disabled={!isComplete}>
              {t('MenuProgressModal.done')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
